// PreviewModal.js - Förhandsgranskning före signering
import React, { useState, useEffect } from 'react';
import './PreviewModal.css';

const PreviewModal = ({
  journalData,
  sections = [],
  patientInfo,
  onSign,
  onClose,
  signing = false
}) => {
  const [checklist, setChecklist] = useState([]);

  useEffect(() => {
    // Initialize checklist based on journal content
    const items = [
      {
        id: 'patient_correct',
        label: 'Rätt patient',
        description: 'Kontrollera att journalen tillhör rätt patient',
        required: true,
        checked: false
      },
      {
        id: 'content_reviewed',
        label: 'Innehåll granskat',
        description: 'Jag har läst igenom hela journalanteckningen',
        required: true,
        checked: false
      },
      {
        id: 'sections_complete',
        label: 'Sektioner ifyllda',
        description: `${filledSections.length} av ${sections.length} sektioner har innehåll`,
        required: false,
        checked: filledSections.length === sections.length,
        warning: filledSections.length < sections.length
      },
      {
        id: 'no_placeholders',
        label: 'Inga platshållare',
        description: 'Kontrollera att [XXXX] och liknande är ersatta',
        required: false,
        checked: !hasPlaceholders,
        warning: hasPlaceholders
      },
      {
        id: 'understand_permanent',
        label: 'Förstår att signering är permanent',
        description: 'Journalen kan inte ändras efter signering',
        required: true,
        checked: false
      }
    ];

    setChecklist(items);
  }, [sections]);

  // Calculate filled sections
  const filledSections = sections.filter(s => s.content && s.content.trim().length > 0);

  // Check for placeholders like [XXX], {TODO}, etc.
  const allContent = sections.map(s => s.content || '').join(' ');
  const hasPlaceholders = /\[[^\]]+\]|\{[^}]+\}|TODO|FIXME/i.test(allContent);

  // Count words
  const wordCount = allContent.split(/\s+/).filter(w => w.length > 0).length;

  const handleCheckItem = (itemId) => {
    setChecklist(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const requiredChecked = checklist.filter(i => i.required).every(i => i.checked);
  const allChecked = checklist.every(i => i.checked);
  const checkedCount = checklist.filter(i => i.checked).length;
  const progress = (checkedCount / checklist.length) * 100;

  const handleSign = () => {
    if (!requiredChecked) return;
    onSign();
  };

  return (
    <div className="preview-modal-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="preview-modal-header">
          <h2>
            <i className="fas fa-eye"></i>
            Förhandsgranskning före signering
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="preview-modal-body">
          {/* Left side - Journal preview */}
          <div className="preview-content">
            {patientInfo && (
              <div className="preview-patient-info">
                <div className="preview-patient-name">
                  <i className="fas fa-user"></i>
                  {patientInfo.name}
                </div>
                <div className="preview-patient-details">
                  {patientInfo.personnummer && (
                    <span>
                      <i className="fas fa-id-card"></i>
                      {patientInfo.personnummer}
                    </span>
                  )}
                  {patientInfo.date && (
                    <span>
                      <i className="fas fa-calendar"></i>
                      {new Date(patientInfo.date).toLocaleDateString('sv-SE')}
                    </span>
                  )}
                  {journalData?.template_name && (
                    <span>
                      <i className="fas fa-file-alt"></i>
                      {journalData.template_name}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="preview-stats">
              <div className="preview-stat">
                <i className="fas fa-list"></i>
                <span className="preview-stat-value">{sections.length}</span> sektioner
              </div>
              <div className="preview-stat">
                <i className="fas fa-check-circle"></i>
                <span className="preview-stat-value">{filledSections.length}</span> ifyllda
              </div>
              <div className="preview-stat">
                <i className="fas fa-font"></i>
                <span className="preview-stat-value">{wordCount}</span> ord
              </div>
            </div>

            {sections.map((section, idx) => (
              <div key={idx} className="preview-section">
                <div className="preview-section-title">
                  <i className="fas fa-chevron-right"></i>
                  {section.title || `Sektion ${idx + 1}`}
                </div>
                {section.content && section.content.trim() ? (
                  <div className="preview-section-content">
                    {section.content}
                  </div>
                ) : (
                  <div className="preview-section-empty">
                    (Ingen text)
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right side - Checklist */}
          <div className="preview-checklist">
            <div className="checklist-title">
              <i className="fas fa-tasks"></i>
              Checklista före signering
            </div>

            <div className="checklist-items">
              {checklist.map((item) => (
                <div
                  key={item.id}
                  className={`checklist-item ${item.checked ? 'checked' : ''} ${item.required ? 'required' : ''} ${item.warning && !item.checked ? 'warning' : ''}`}
                  onClick={() => handleCheckItem(item.id)}
                >
                  <div className="checklist-checkbox">
                    <i className="fas fa-check"></i>
                  </div>
                  <div className="checklist-label">
                    <div className="checklist-label-text">{item.label}</div>
                    <div className="checklist-label-desc">{item.description}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="checklist-progress">
              <div className="checklist-progress-bar">
                <div
                  className={`checklist-progress-fill ${!allChecked ? 'incomplete' : ''}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="checklist-progress-text">
                {checkedCount} av {checklist.length} punkter klara
              </div>
            </div>
          </div>
        </div>

        <div className="preview-modal-footer">
          <div className="preview-footer-info">
            <i className="fas fa-info-circle"></i>
            {requiredChecked
              ? 'Alla obligatoriska punkter är bekräftade'
              : 'Bekräfta obligatoriska punkter (*) för att signera'}
          </div>
          <div className="preview-footer-actions">
            <button className="preview-cancel-btn" onClick={onClose}>
              Avbryt
            </button>
            <button
              className="preview-sign-btn"
              onClick={handleSign}
              disabled={!requiredChecked || signing}
            >
              {signing ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Signerar...
                </>
              ) : (
                <>
                  <i className="fas fa-signature"></i>
                  Signera journal
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
